import React, { useState, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconUpload, IconClock, IconWand, IconDownload, IconCheck } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import KnowledgeDiagnostic from '../components/KnowledgeDiagnostic';
import AgentsFlowService from '../../../services/AgentsFlowService';

const TEXT_EXTENSIONS = ['.pdf', '.txt', '.docx'];
const MAX_CORRECT_SIZE = 500 * 1024; // 500KB

const KnowledgeBaseStep = ({ wizardState, setWizardState }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [correctedContent, setCorrectedContent] = useState(null);
  const [correctError, setCorrectError] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const { knowledgeOption, knowledgeDiagnostic } = wizardState;

  const handleOptionSelect = (option) => {
    setWizardState((prev) => ({
      ...prev,
      knowledgeOption: option,
      knowledgeDiagnostic: option === 'later' ? null : prev.knowledgeDiagnostic,
      knowledgeFileKey: option === 'later' ? null : prev.knowledgeFileKey,
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho no client-side (1MB)
    if (file.size > 1 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo: 1MB.');
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await AgentsFlowService.wizardAnalyzeKnowledge(formData);

      setWizardState((prev) => ({
        ...prev,
        knowledgeDiagnostic: response.data,
        knowledgeFileKey: response.data.file_key,
        knowledgeOption: 'upload',
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao analisar arquivo. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  // Check if file is eligible for FAQ correction
  const canCorrectFAQ = (() => {
    if (!knowledgeDiagnostic) return false;
    const fileName = knowledgeDiagnostic.file_name || '';
    const fileSize = knowledgeDiagnostic.file_size || 0;
    const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return TEXT_EXTENSIONS.includes(ext) && fileSize <= MAX_CORRECT_SIZE;
  })();

  const handleCorrectFAQ = async () => {
    if (!wizardState.knowledgeFileKey) return;
    setIsCorrecting(true);
    setCorrectError(null);
    setCorrectedContent(null);

    Swal.fire({
      title: 'Corrigindo FAQ...',
      html: '<p style="margin-bottom:8px">A IA está reestruturando seu arquivo.</p><p style="color:#64748b;font-size:13px">Esse processo pode levar alguns minutos.<br>Por favor, não feche esta janela.</p>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const response = await AgentsFlowService.wizardCorrectFAQ({
        file_key: wizardState.knowledgeFileKey,
      });
      setCorrectedContent(response.data.corrected_content);
      Swal.fire({
        icon: 'success',
        title: 'FAQ corrigido!',
        text: 'O arquivo foi reestruturado com sucesso. Baixe o novo arquivo abaixo.',
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      setCorrectError(err.response?.data?.error || 'Erro ao corrigir FAQ. Tente novamente.');
      Swal.fire({
        icon: 'error',
        title: 'Erro ao corrigir',
        text: err.response?.data?.error || 'Não foi possível corrigir o FAQ. Tente novamente.',
      });
    } finally {
      setIsCorrecting(false);
    }
  };

  const handleDownloadCorrected = () => {
    if (!correctedContent) return;
    const blob = new Blob([correctedContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faq-corrigido.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h3 className="awc-body__title">Base de Conhecimento</h3>
      <p className="awc-body__subtitle">
        Uma base de conhecimento ajuda o agente a responder com informações específicas do seu negócio.
      </p>

      <div className="awc-options__grid">
        <div
          className={`awc-options__card${knowledgeOption === 'upload' ? ' awc-options__card--selected' : ''}`}
          onClick={() => {
            handleOptionSelect('upload');
            if (!knowledgeDiagnostic) {
              fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Enviar arquivo de conhecimento"
        >
          <div className="awc-options__icon">
            <IconUpload size={32} />
          </div>
          <h4>Tenho um arquivo</h4>
          <p>Envie um CSV, PDF, DOCX, XLSX ou TXT com informações do seu negócio</p>
        </div>

        <div
          className={`awc-options__card${knowledgeOption === 'later' ? ' awc-options__card--selected' : ''}`}
          onClick={() => handleOptionSelect('later')}
          role="button"
          tabIndex={0}
          aria-label="Adicionar base de conhecimento depois"
        >
          <div className="awc-options__icon awc-options__icon--muted">
            <IconClock size={32} />
          </div>
          <h4>Vou adicionar depois</h4>
          <p>Você pode adicionar a base de conhecimento a qualquer momento após criar o agente</p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.docx,.txt,.pdf"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {knowledgeOption === 'upload' && !knowledgeDiagnostic && !isUploading && (
        <div className="text-center mt-3">
          <button
            className="awc-btn awc-btn--outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconUpload size={16} />
            Selecionar arquivo
          </button>
          <p className="awc-loading__subtext mt-2">
            Formatos aceitos: CSV, XLSX, DOCX, TXT, PDF (máx. 1MB)
          </p>
        </div>
      )}

      {isUploading && (
        <div className="awc-loading" aria-live="polite">
          <div className="awc-loading__icon">
            <Spinner animation="border" />
          </div>
          <p className="awc-loading__text">Analisando seu arquivo...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
          <button
            className="awc-btn awc-btn--link"
            onClick={() => fileInputRef.current?.click()}
          >
            Tentar outro arquivo
          </button>
        </div>
      )}

      <KnowledgeDiagnostic diagnostic={knowledgeDiagnostic} />

      {knowledgeDiagnostic && canCorrectFAQ && !correctedContent && (
        <div className="awc-faq-correct">
          <div className="awc-faq-correct__info">
            <IconWand size={18} className="awc-faq-correct__icon" />
            <div>
              <p className="awc-faq-correct__title">Corrigir FAQ com IA</p>
              <p className="awc-faq-correct__desc">
                A IA vai reestruturar o arquivo no formato ideal de Perguntas e Respostas para melhor compreensão do agente.
              </p>
            </div>
          </div>
          <button
            className="awc-btn awc-btn--outline"
            onClick={handleCorrectFAQ}
            disabled={isCorrecting}
          >
            {isCorrecting ? (
              <>
                <Spinner animation="border" size="sm" />
                Corrigindo...
              </>
            ) : (
              <>
                <IconWand size={16} />
                Corrigir FAQ
              </>
            )}
          </button>
          {correctError && (
            <p className="awc-faq-correct__error">{correctError}</p>
          )}
        </div>
      )}

      {correctedContent && (
        <div className="awc-faq-correct awc-faq-correct--success">
          <div className="awc-faq-correct__info">
            <IconCheck size={18} className="awc-faq-correct__icon awc-faq-correct__icon--success" />
            <div>
              <p className="awc-faq-correct__title">FAQ corrigido com sucesso!</p>
              <p className="awc-faq-correct__desc">
                Baixe o arquivo corrigido e envie-o novamente para obter melhores resultados.
              </p>
            </div>
          </div>
          <button
            className="awc-btn awc-btn--primary"
            onClick={handleDownloadCorrected}
          >
            <IconDownload size={16} />
            Baixar FAQ Corrigido
          </button>
        </div>
      )}

      {knowledgeOption === 'later' && (
        <div className="alert alert-info mt-3">
          Sem problema! Após criar o agente, acesse a aba "Conhecimento" no painel lateral para adicionar arquivos.
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseStep;
